import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { useAuth } from '../contexts/AuthContext';
import GastosTable from '../components/GastosTable';
import GastosChart from '../components/GastosChart';
import SummaryCards from '../components/SummaryCards';
import ContaModal from '../components/ContaModal';
import './DashboardPage.css';

const CURRENT_YEAR = new Date().getFullYear();
const MONTHS = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];

export default function DashboardPage() {
  const { user, profile } = useAuth();
  const [ano, setAno] = useState(CURRENT_YEAR);
  const [mesSel, setMesSel] = useState(new Date().getMonth());
  const [view, setView] = useState('tabela');
  const [contas, setContas] = useState([]);
  const [lancamentos, setLancamentos] = useState([]);
  const [metas, setMetas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editConta, setEditConta] = useState(null);
  const [saving, setSaving] = useState(false);
  const [tiposConta, setTiposConta] = useState([]);
  const [hideValues, setHideValues] = useState(false);
  const [accessibleIds, setAccessibleIds] = useState([]);

  useEffect(() => {
    if (!profile) return;
    const ids = [profile.id];
    if (profile.shared_with) ids.push(profile.shared_with);
    supabase
      .from('profiles')
      .select('id')
      .eq('shared_with', profile.id)
      .then(({ data }) => {
        if (data) data.forEach(p => { if (!ids.includes(p.id)) ids.push(p.id); });
        setAccessibleIds(ids);
      });
  }, [profile]);

  const loadData = useCallback(async () => {
    if (!accessibleIds.length) return;
    setLoading(true);

    const seisAtras = new Date();
    seisAtras.setMonth(seisAtras.getMonth() - 6);
    supabase.from('historico_atividades')
      .delete()
      .lt('created_at', seisAtras.toISOString())
      .then(() => {});

    const [contasRes, lancRes, metasRes, tiposRes] = await Promise.all([
      supabase.from('contas').select('*').in('owner_id', accessibleIds).eq('ativa', true).order('nome'),
      supabase.from('lancamentos').select('*').in('owner_id', accessibleIds).eq('ano', ano),
      supabase.from('meta_mensal').select('*').in('owner_id', accessibleIds).eq('ano', ano),
      supabase.from('tipos_conta').select('*').in('owner_id', accessibleIds).order('nome'),
    ]);

    setContas(contasRes.data || []);
    setLancamentos(lancRes.data || []);
    setMetas(metasRes.data || []);
    setTiposConta(tiposRes.data || []);
    setLoading(false);
  }, [accessibleIds, ano]);

  useEffect(() => { loadData(); }, [loadData]);

  async function registrarAtividade(acao, descricao) {
    try {
      await supabase.from('historico_atividades').insert({
        owner_id: user.id,
        usuario_nome: profile?.name || 'Usuário',
        acao,
        descricao,
      });
    } catch (e) {}
  }

  async function handleValorChange(contaId, mes, valor, tipoLancamento = 'saida') {
    const existing = lancamentos.find(l => l.conta_id === contaId && l.mes === mes + 1);
    const ownerOfConta = contas.find(c => c.id === contaId)?.owner_id || user.id;
    const payload = {
      conta_id: contaId, owner_id: ownerOfConta, ano, mes: mes + 1,
      valor: parseFloat(valor) || 0, tipo_lancamento: tipoLancamento,
    };
    setSaving(true);
    if (existing) {
      await supabase.from('lancamentos').update({ valor: payload.valor, tipo_lancamento: tipoLancamento }).eq('id', existing.id);
      setLancamentos(prev => prev.map(l => l.id === existing.id ? { ...l, valor: payload.valor, tipo_lancamento: tipoLancamento } : l));
    } else {
      const { data } = await supabase.from('lancamentos').insert(payload).select().single();
      if (data) setLancamentos(prev => [...prev, data]);
    }
    const contaNome = contas.find(c => c.id === contaId)?.nome || 'Conta';
    const mesNome = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'][mes];
    const valorFmt = parseFloat(valor).toLocaleString('pt-BR', { style:'currency', currency:'BRL' });
    const tipoLabel = tipoLancamento === 'entrada' ? 'Entrada' : 'Saída';
    await registrarAtividade('valor_alterado', `${tipoLabel} registrada: ${contaNome} — ${mesNome}/${ano} → ${valorFmt}`);
    setSaving(false);
  }

  async function handleToggleProjecao(contaId, mes) {
    const existing = lancamentos.find(l => l.conta_id === contaId && l.mes === mes + 1);
    if (!existing) return;
    const novoEstado = !existing.is_projecao;
    await supabase.from('lancamentos').update({ is_projecao: novoEstado }).eq('id', existing.id);
    setLancamentos(prev => prev.map(l =>
      l.id === existing.id ? { ...l, is_projecao: novoEstado } : l
    ));
    const contaNome = contas.find(c => c.id === contaId)?.nome || 'Conta';
    const mesNome = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'][mes];
    const valorFmt = (existing.valor || 0).toLocaleString('pt-BR', { style:'currency', currency:'BRL' });
    await registrarAtividade(
      'valor_alterado',
      novoEstado
        ? `Projeção marcada: ${contaNome} — ${mesNome}/${ano} → ${valorFmt}`
        : `Projeção removida: ${contaNome} — ${mesNome}/${ano} → ${valorFmt}`
    );
  }

  async function handleMetaChange(mes, valor) {
    const existing = metas.find(m => m.mes === mes + 1 && m.owner_id === user.id);
    const payload = { owner_id: user.id, ano, mes: mes + 1, valor_referencia: parseFloat(valor) || 0 };
    setSaving(true);
    if (existing) {
      await supabase.from('meta_mensal').update({ valor_referencia: payload.valor_referencia }).eq('id', existing.id);
      setMetas(prev => prev.map(m => m.id === existing.id ? { ...m, valor_referencia: payload.valor_referencia } : m));
    } else {
      const { data } = await supabase.from('meta_mensal').insert(payload).select().single();
      if (data) setMetas(prev => [...prev, data]);
    }
    await registrarAtividade('limite_alterado', `Limite mensal alterado para R$ ${parseFloat(valor).toLocaleString('pt-BR', {minimumFractionDigits:2})}`);
    setSaving(false);
  }

  async function handleSaveConta(conta) {
    if (editConta) {
      await supabase.from('contas').update(conta).eq('id', editConta.id);
      await registrarAtividade('conta_editada', `Conta editada: ${conta.nome}`);
    } else {
      await supabase.from('contas').insert({ ...conta, owner_id: user.id });
      await registrarAtividade('conta_criada', `Nova conta criada: ${conta.nome}`);
    }
    setShowModal(false);
    setEditConta(null);
    await loadData();
  }

  async function handleDeleteConta(contaId) {
    if (!window.confirm('Excluir esta conta? Os lançamentos também serão removidos.')) return;
    const contaNome = contas.find(c => c.id === contaId)?.nome || 'Conta';
    await supabase.from('contas').update({ ativa: false }).eq('id', contaId);
    await registrarAtividade('conta_excluida', `Conta excluída: ${contaNome}`);
    await loadData();
  }

  const years = Array.from({ length: 5 }, (_, i) => CURRENT_YEAR - 2 + i);

  // Totais — exclui projeções, inclui custom
  const cartaoContas = contas.filter(c => c.tipo === 'cartao');
  const fixoContas = contas.filter(c => c.tipo === 'fixo');
  const outroContas = contas.filter(c => c.tipo === 'outro');
  const customContas = contas.filter(c => c.tipo === 'custom');

  function sumContas(lista, mes) {
    return lista.reduce((acc, c) => {
      const l = lancamentos.find(l => l.conta_id === c.id && l.mes === mes + 1);
      if (l?.is_projecao) return acc;
      return acc + (l?.valor || 0);
    }, 0);
  }

  const totalCartao = sumContas(cartaoContas, mesSel);
  const totalFixo = sumContas(fixoContas, mesSel);
  const totalOutro = sumContas(outroContas, mesSel);
  const totalCustom = sumContas(customContas, mesSel);
  const totalGeral = totalCartao + totalFixo + totalOutro + totalCustom;
  const metaMes = metas.find(m => m.mes === mesSel + 1)?.valor_referencia || 0;
  const diferenca = metaMes - totalGeral;

  return (
    <div className="dashboard fade-in">
      <div className="dash-header">
        <div className="dash-title-row">
          <h2 className="dash-title">Meu Controle de Gastos</h2>
          {saving && <span className="saving-badge"><span className="spinner" /> salvando...</span>}
        </div>
        <div className="dash-controls">
          <div className="year-month-sel" style={{ display:'flex', gap:8, alignItems:'center' }}>
            <button
              className={`btn btn-sm ${hideValues ? 'btn-primary' : 'btn-ghost'}`}
              onClick={() => setHideValues(h => !h)}
              title={hideValues ? 'Mostrar valores' : 'Ocultar valores'}
            >
              {hideValues ? '◉ Mostrar' : '◎ Ocultar'}
            </button>
            <select value={ano} onChange={e => setAno(+e.target.value)} className="sel-year">
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div className="view-toggle">
            <button className={`btn btn-sm ${view === 'tabela' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setView('tabela')}>⊞ Tabela</button>
            <button className={`btn btn-sm ${view === 'grafico' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setView('grafico')}>⟁ Gráfico</button>
          </div>
          <button className="btn btn-primary btn-sm" onClick={() => { setEditConta(null); setShowModal(true); }}>
            + Nova Conta
          </button>
        </div>
      </div>

      <div className="months-tabs">
        {MONTHS.map((m, i) => (
          <button key={i} className={`month-tab ${mesSel === i ? 'active' : ''}`} onClick={() => setMesSel(i)}>
            {m.slice(0,3)}
          </button>
        ))}
      </div>

      <SummaryCards
        totalCartao={totalCartao}
        totalFixo={totalFixo}
        totalOutro={totalOutro}
        totalGeral={totalGeral}
        meta={metaMes}
        diferenca={diferenca}
        mes={mesSel}
        onMetaChange={handleMetaChange}
        hideValues={hideValues}
      />

      {loading ? (
        <div className="loading-state">
          <div className="spinner" style={{ width:36, height:36 }} />
          <span>Carregando dados...</span>
        </div>
      ) : view === 'tabela' ? (
        <GastosTable
          contas={contas}
          lancamentos={lancamentos}
          metas={metas}
          ano={ano}
          mesSel={mesSel}
          onValorChange={handleValorChange}
          onToggleProjecao={handleToggleProjecao}
          onMetaChange={handleMetaChange}
          onEditConta={(c) => { setEditConta(c); setShowModal(true); }}
          onDeleteConta={handleDeleteConta}
          tiposCustom={tiposConta}
          hideValues={hideValues}
        />
      ) : (
        <GastosChart contas={contas} lancamentos={lancamentos} metas={metas} ano={ano} />
      )}

      {showModal && (
        <ContaModal
          conta={editConta}
          tiposCustom={tiposConta}
          onSave={handleSaveConta}
          onClose={() => { setShowModal(false); setEditConta(null); }}
        />
      )}
    </div>
  );
}
